import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnChanges,
  OnDestroy,
  InputSignal,
  input,
  OutputEmitterRef,
  output,
  inject
} from '@angular/core';
import { TranslateDirective } from '@ngx-translate/core';
import { Subscription, Subject } from 'rxjs';

import { BrightcovePlayerComponent } from '../../../features/brightcove-player/brightcove-player.component';
import { TabComponent } from '../../../components/tabs/tab.component';
import { DataAnalyticsTaggingConstants } from '../../../features/data-analytics-tagging/data-analytics-tagging.constants';
import { CourseLesson } from '../../../domain/learn/learn.domain';
import { TimezoneService } from '../../../services/date/timezone.service';
import { ScrollableDirective } from '../../../components/scroll/scrollable.directive';
import { TabsComponent } from '../../../components/tabs/tabs.component';
import { DataAnalyticsTaggingDirective } from '../../../features/data-analytics-tagging/data-analytics-tagging.directive';
import { BrightcoveCue } from '../../../features/brightcove-player/brightcove-player.domain';
import { DataAnalyticsTaggingEvent } from '../../../features/data-analytics-tagging/data-analytics-tagging.domain';

import { LearnLessonInfoComponent } from '../lesson-info/learn-lesson-info.component';
import { LearnProgress, LearnProgressforRecommendation } from '../learn.domain';
import { LearnProgressService } from './../learn-progress.service';

export type LearnPlayerTab = 'overview' | 'transcript';

@Component({
  selector: 'td-wb-learn-player',
  templateUrl: 'learn-player.component.html',
  styleUrls: ['learn-player.component.scss'],
  imports: [
    NgTemplateOutlet,
    TabsComponent,
    TabComponent,
    LearnLessonInfoComponent,
    TranslateDirective,
    ScrollableDirective,
    BrightcovePlayerComponent,
    DataAnalyticsTaggingDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LearnPlayerComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly THRESHOLD_FOR_RECOMMENDATIONS: number = 20000;

  lesson: InputSignal<CourseLesson> = input<CourseLesson>();
  section: InputSignal<string> = input<string>();
  autoPlay: InputSignal<boolean> = input<boolean>();
  lessonView: InputSignal<boolean> = input<boolean>();
  hideFilters: InputSignal<boolean> = input<boolean>();
  progressChange: OutputEmitterRef<number> = output<number>();
  pausedChange: OutputEmitterRef<boolean> = output<boolean>();

  selectedTab: LearnPlayerTab = 'overview';
  progress: number = 0;
  sentDataForRecommendation: boolean = false;
  readonly timeupdateEnabled: boolean = true;

  private timeUpdateSubscription: Subscription = Subscription.EMPTY;
  private readonly timeUpdateSubject: Subject<number> = new Subject<number>();
  private cuePoint: number;
  private timer: ReturnType<typeof setTimeout>;
  private startProgressUpdate: boolean = false;
  eventData: DataAnalyticsTaggingEvent = {};

  private learnProgressService: LearnProgressService = inject(LearnProgressService);
  private timezoneService: TimezoneService = inject(TimezoneService);

  ngOnInit(): void {
    this.setAnalytics();
  }

  ngOnChanges(): void {
    this.progress = 0;
    this.sentDataForRecommendation = false;
    this.cuePoint = undefined;
    if (this.lesson() !== undefined) {
      this.progress = this.lesson().progress === this.lesson().duration ? 0 : this.lesson().progress;
    }
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
    }
  }

  private setAnalytics(): void {
    const courseSections: Array<string> = [ 'Learning Paths', 'Video Courses' ];
    const lessonType = courseSections.includes(this.section()) ? 'course' : 'lesson';
    this.eventData = DataAnalyticsTaggingConstants.getVideoPageEventData(lessonType, this.section(), this.lesson()?.title);
  }

  setActiveTab(selectedTabId: LearnPlayerTab): void {
    this.selectedTab = selectedTabId;
    if (this.section){
      this.eventData = DataAnalyticsTaggingConstants.getLinkClickEventData(`tdw:wbt:tabs:${this.section}:${selectedTabId}`);
    } else {//For EasyTrade
      this.eventData = DataAnalyticsTaggingConstants.getLinkClickEventData(`tdw:et:tabs:${selectedTabId}`);
    }
  }

  ngOnDestroy(): void {
    this.timeUpdateSubscription.unsubscribe();
  }

  /**
   * updated progress, if timeUpdateSubscription was closed by onCueChange
   * Setup progress listener again
   */
  timeupdate(time: number): void {
    if (this.startProgressUpdate === true) {
      if (this.cuePoint === undefined || time < this.cuePoint) {
        if (this.timeUpdateSubscription.closed) {
          this.setProgress();
        }
        this.timeUpdateSubject.next(time);
      }
    }
  }

  /**
   * onPausedChange video play pause
   */
  onPausedChange(value: boolean): void {
    if (this.startProgressUpdate === false) {
      this.startProgressUpdate = !value;
    }
    this.pausedChange.emit(value);
  }

  /**
   * onCueChange set progress equal to duration
   * unsubscribe progress
   */
  onCueChange(cue: BrightcoveCue): void {
    this.timeUpdateSubscription.unsubscribe();
    this.cuePoint = cue.startTime * 1000;
    this.postProgressdata(this.lesson().duration);

    const videoCompleteTime = this.lesson().duration - this.cuePoint;
    this.timer = setTimeout((): void => {
      this.progressChange.emit(this.lesson().duration);
    }, videoCompleteTime);
  }

  private setProgress(): void {
    this.timeUpdateSubscription = this.timeUpdateSubject
      .subscribe((time: number): void => {
        this.postProgressdata(time);
        this.progressChange.emit(time);
      });
  }

  private postProgressdata(time: number): void {
    if (this.sentDataForRecommendation === false
      && time >= LearnPlayerComponent.THRESHOLD_FOR_RECOMMENDATIONS) {
      const watchedDataRecommendation: LearnProgressforRecommendation = {
        videoId: this.lesson().contentMetaData.id,
        watched: this.timezoneService.getCurrentDateInServerTimezone(),
        completed: this.lesson().completion
      };
      this.learnProgressService.sendDataforRecommendation(watchedDataRecommendation);
      this.sentDataForRecommendation = true;
    }
    if (this.lesson().progress !== time) {
      this.lesson().progress = time;
      if (this.lesson().completion === undefined && this.lesson().progress === this.lesson().duration) {
        this.lesson().completion = this.timezoneService.getCurrentDateInServerTimezone();

        if (time >= LearnPlayerComponent.THRESHOLD_FOR_RECOMMENDATIONS) {
          const completedDataRecommendation: LearnProgressforRecommendation = {
            videoId: this.lesson().contentMetaData.id,
            completed: this.lesson().completion
          };
          this.learnProgressService.sendDataforRecommendation(completedDataRecommendation);
        }
      }

      const progress: LearnProgress = {
        videoId: this.lesson().contentMetaData.id,
        progress: this.lesson().progress,
        completion: this.lesson().completion
      };
      this.learnProgressService.sendProgress(progress);
    }
  }
}
